import React, { useCallback, useEffect, useRef, useState } from "react";

import { VisualizerSidebar } from "..";
import { useGetAllVisualizationsQuery } from "../../services/visualizations";
import { ThreeDView } from "./components/3DView";
import { ChartView } from "./components/ChartView";
import { ResizableDivider } from "./components/ResizableDivider";
import { useAvatarControls } from "./hooks/useAvatarControls";
import { generateDynamicColorPalette } from "./utils/colorPalette";
import { processVisualizationData } from "./utils/dataProcessing";

export type VisualizerSessionInfo = {
  playerName: string;
  orgPlayerId: string;
  domHand: string;
  sessionId: string;
  sessionDate: string;
  movementTypeId?: string;
};

interface DataVisualizerProps {
  sessionInfo: VisualizerSessionInfo;
}

interface Pitch {
  id: string;
  frames: any[];
  metadata: Record<string, any>;
}

interface MovementItem {
  id: string;
  sessionDate: string;
  movementNum: string;
  pitchType: string;
  domHand: string;
  speed: string;
  color: string;
  isSelected: boolean;
}

export const DataVisualizer: React.FC<DataVisualizerProps> = ({
  sessionInfo,
}) => {
  // Determine movement type and mode
  const movementTypeId = sessionInfo.movementTypeId
    ? parseInt(sessionInfo.movementTypeId, 10)
    : 2; // Default to pitching
  const isHittingMode = movementTypeId === 1;

  const {
    data: visualizations,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllVisualizationsQuery({
    orgPlayerId: sessionInfo.orgPlayerId,
    sessionId: sessionInfo.sessionId,
    movementTypeId,
  });

  // Handle 404 errors: transform error message and prevent retry
  const is404Error =
    error &&
    typeof error === "object" &&
    "status" in error &&
    error.status === 404;
  const displayError = is404Error ? { message: "No data found." } : error;
  const handleRetry = is404Error ? undefined : refetch;

  // Refs
  const canvas3dRef = useRef<HTMLCanvasElement>(null);
  const animationIdRef = useRef<number | null>(null);

  // Data state
  const [pitches, setPitches] = useState<Record<string, Pitch>>({});
  const [movements, setMovements] = useState<MovementItem[]>([]);
  const [pitchTypes, setPitchTypes] = useState<string[]>([]);
  const [domHands, setDomHands] = useState<string[]>([]);
  const [dataColumns, setDataColumns] = useState<string[]>([]);
  const [timeSeriesColumns, setTimeSeriesColumns] = useState<string[]>([]);
  const [selectedPitches, setSelectedPitches] = useState<Set<string>>(
    new Set(),
  );
  const [colorPalette, setColorPalette] = useState<string[]>([]);

  // Avatar controls
  const {
    avatarOffsets,
    avatarRotations,
    handleAvatarOffsetChange,
    handleAvatarRotationChange,
    handleResetAvatarPosition,
    handleResetAvatarRotation,
    handleResetAllAvatar,
  } = useAvatarControls();

  // Time and playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [minTime, setMinTime] = useState(-2);
  const [maxTime, setMaxTime] = useState(1);
  const [currentTime, setCurrentTime] = useState(-2);

  // Resizable divider state
  const mainContentRef = useRef<HTMLDivElement | null>(null);
  const [topSectionHeight, setTopSectionHeight] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  // Process data from API response
  const processData = useCallback((apiResponse: any) => {
    const data = apiResponse || [];

    // Clear existing state first
    setPitches({});
    setMovements([]);
    setPitchTypes([]);
    setDomHands([]);
    setDataColumns([]);
    setTimeSeriesColumns([]);

    // Process data using utility functions
    const processedData = processVisualizationData(data);

    // Update state with processed data
    setPitches(processedData.pitches);
    setPitchTypes(processedData.pitchTypes);
    setDomHands(processedData.domHands);
    setDataColumns(processedData.dataColumns);
    setTimeSeriesColumns(processedData.timeSeriesColumns);

    // Update global time bounds
    if (
      processedData.globalMinTime !== Infinity &&
      processedData.globalMaxTime !== -Infinity
    ) {
      // Ensure we have a valid time range
      if (processedData.globalMaxTime > processedData.globalMinTime) {
        setMinTime(processedData.globalMinTime);
        setMaxTime(processedData.globalMaxTime);
      } else {
        // Fallback to reasonable defaults if data has issues
        setMinTime(0);
        setMaxTime(1);
      }
    }
  }, []);

  // Process data from visualizations when loading is complete
  useEffect(() => {
    if (visualizations && !isLoading) {
      processData(visualizations);
    }
  }, [processData, visualizations, isLoading]);

  // Update currentTime when minTime changes (for initial data load)
  useEffect(() => {
    setCurrentTime(minTime);
  }, [minTime]);

  // Generate movement list from pitches
  const generateMovements = useCallback(
    (pitchesData: Record<string, Pitch>) => {
      const movementsList: MovementItem[] = [];
      const pitchIds = Object.keys(pitchesData);

      // Generate dynamic color palette based on number of movements
      const colors = generateDynamicColorPalette(pitchIds.length);
      setColorPalette(colors);

      for (let i = 0; i < pitchIds.length; i++) {
        const pitchId = pitchIds[i];
        const pitch = pitchesData[pitchId];
        const color = colors[i];

        const speed = pitch.metadata.rel_speed_mph
          ? pitch.metadata.rel_speed_mph.toFixed(1)
          : "N/A";
        // Use mocap_pitch_type for both pitching and hitting (field name is the same)
        const pitchType = pitch.metadata.mocap_pitch_type || "Unknown";
        const domHand = pitch.metadata.dom_hand || "Unknown";

        movementsList.push({
          id: pitchId,
          sessionDate: pitch.metadata.session_date || "Unknown",
          movementNum: pitch.metadata.movement_num || "Unknown",
          pitchType,
          domHand,
          speed,
          color,
          isSelected: selectedPitches.has(pitchId),
        });
      }

      // Merge with existing movements to preserve user-selected colors
      setMovements((prevMovements) => {
        const movementsMap = new Map(prevMovements.map((m) => [m.id, m]));

        return movementsList.map((newMovement) => {
          const existing = movementsMap.get(newMovement.id);
          if (existing?.color) {
            return { ...newMovement, color: existing.color };
          }
          return newMovement;
        });
      });
    },
    [selectedPitches],
  );

  // Update movements when pitches change
  useEffect(() => {
    if (Object.keys(pitches).length > 0) {
      generateMovements(pitches);
    }
  }, [pitches, generateMovements]);

  // Animation loop for playback
  useEffect(() => {
    if (isPlaying) {
      const animate = () => {
        setCurrentTime((prevTime) => {
          const timeStep = 0.016 * playbackSpeed; // 60fps base
          const newTime = prevTime + timeStep;

          if (newTime >= maxTime) {
            // Loop back to start for continuous playback
            return minTime;
          }

          return newTime;
        });

        animationIdRef.current = requestAnimationFrame(animate);
      };
      animate();
    } else if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
    }

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, maxTime, minTime]);

  // Reset time to start when no movements are selected
  useEffect(() => {
    if (selectedPitches.size === 0) {
      setCurrentTime(minTime);
    }
  }, [selectedPitches.size, minTime]);

  const handlePlayPause = useCallback(() => {
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handlePlaybackSpeedChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setPlaybackSpeed(Number(e.target.value));
    },
    [],
  );

  const handleSkipBackward = useCallback(() => {
    setCurrentTime(Math.max(minTime, currentTime - 0.1));
  }, [minTime, currentTime]);

  const handleSkipForward = useCallback(() => {
    setCurrentTime(Math.min(maxTime, currentTime + 0.1));
  }, [maxTime, currentTime]);

  const handleMovementClick = useCallback(
    (movementId: string) => {
      const newSelectedPitches = new Set(selectedPitches);
      if (newSelectedPitches.has(movementId)) {
        newSelectedPitches.delete(movementId);
      } else {
        newSelectedPitches.add(movementId);
      }
      setSelectedPitches(newSelectedPitches);
    },
    [selectedPitches],
  );

  const handleColorChange = (movementId: string, newColor: string) => {
    setMovements((prev) => {
      return prev.map((m) =>
        m.id === movementId ? { ...m, color: newColor } : m,
      );
    });
  };

  // Bulk handlers for SelectionsPanel (apply to all selected movements)
  const handleColorChangeAll = useCallback(
    (newColor: string) => {
      setMovements((prev) =>
        prev.map((m) =>
          selectedPitches.has(m.id) ? { ...m, color: newColor } : m,
        ),
      );
    },
    [selectedPitches],
  );

  const handleOffsetChangeAll = useCallback(
    (axis: "x" | "y" | "z", value: number) => {
      selectedPitches.forEach((pitchId) => {
        handleAvatarOffsetChange(pitchId, axis, value);
      });
    },
    [selectedPitches, handleAvatarOffsetChange],
  );

  const handleRotationChangeAll = useCallback(
    (axis: "x" | "y" | "z", value: number) => {
      selectedPitches.forEach((pitchId) => {
        handleAvatarRotationChange(pitchId, axis, value);
      });
    },
    [selectedPitches, handleAvatarRotationChange],
  );

  // Initialize top section height on mount and track container height
  useEffect(() => {
    const initializeHeights = () => {
      if (mainContentRef.current) {
        const height = mainContentRef.current.clientHeight;
        if (height > 0) {
          setContainerHeight(height);

          if (topSectionHeight === null) {
            // Use responsive chart height: smaller on small screens to prevent overlap
            // For containers < 800px, use 30% for chart (70% for 3D view)
            // For larger containers, use fixed 384px (original h-96)
            const isSmallScreen = height < 800;
            const chartHeight = isSmallScreen
              ? Math.max(200, height * 0.3) // Minimum 200px, max 30% of container
              : 384; // Original h-96 value for larger screens
            setTopSectionHeight(height - chartHeight);
          }
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is fully rendered
    const timeoutId = setTimeout(initializeHeights, 0);
    return () => clearTimeout(timeoutId);
  }, [topSectionHeight]);

  // Update container height on resize using ResizeObserver with debouncing
  useEffect(() => {
    if (!mainContentRef.current) return;

    let timeoutId: NodeJS.Timeout;
    const resizeObserver = new ResizeObserver((entries) => {
      // Debounce resize updates to prevent excessive re-renders
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          if (height > 0) {
            setContainerHeight((prevHeight) => {
              // Only update if height actually changed significantly (avoid micro-updates)
              return Math.abs(height - prevHeight) > 1 ? height : prevHeight;
            });
          }
        }
      }, 16); // ~60fps throttle
    });

    resizeObserver.observe(mainContentRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  // Update top section height when container resizes to maintain proportions
  // Only update on window resize, not during drag operations
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (
      mainContentRef.current &&
      topSectionHeight !== null &&
      containerHeight > 0 &&
      !isDraggingRef.current
    ) {
      const currentHeight = mainContentRef.current.clientHeight;
      const heightDiff = Math.abs(currentHeight - containerHeight);
      // Only update if there's a significant difference (window resize, not drag)
      if (heightDiff > 5) {
        // Maintain the same percentage if container resizes
        const currentPercentage = topSectionHeight / containerHeight;
        const newHeight = currentHeight * currentPercentage;
        setTopSectionHeight(newHeight);
        setContainerHeight(currentHeight);
      }
    }
  }, [containerHeight, topSectionHeight]);

  const handleDividerResize = useCallback((newHeight: number) => {
    isDraggingRef.current = true;
    setTopSectionHeight(newHeight);
    // Always update container height from ref to ensure accuracy
    if (mainContentRef.current) {
      const currentHeight = mainContentRef.current.clientHeight;
      setContainerHeight((prev) => {
        // Only update if significantly different to avoid unnecessary re-renders
        return Math.abs(currentHeight - prev) > 1 ? currentHeight : prev;
      });
    }
    // Reset dragging flag after a short delay
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 100);
  }, []);

  return (
    <div
      className="font-sans bg-gray-50 text-blue-900 w-full h-full overflow-hidden"
      data-testid="data-visualizer"
    >
      <div
        className="grid grid-cols-[350px_1fr] w-full h-full"
        data-testid="visualizer-container"
      >
        <VisualizerSidebar
          movements={movements}
          pitchTypes={pitchTypes}
          domHands={domHands}
          pitches={pitches}
          avatarOffsets={avatarOffsets}
          avatarRotations={avatarRotations}
          dataColumns={dataColumns}
          colorPalette={colorPalette}
          movementTypeId={movementTypeId}
          sessionInfo={sessionInfo}
          isLoading={isLoading}
          isError={isError}
          error={displayError}
          onRetry={handleRetry}
          onMovementClick={handleMovementClick}
          onColorChange={handleColorChange}
          onAvatarOffsetChange={handleAvatarOffsetChange}
          onAvatarRotationChange={handleAvatarRotationChange}
          onResetAvatarPosition={handleResetAvatarPosition}
          onResetAvatarRotation={handleResetAvatarRotation}
          onResetAllAvatar={handleResetAllAvatar}
        />

        <div
          ref={mainContentRef}
          className="flex flex-col h-full relative min-h-0 overflow-hidden"
          data-testid="main-content"
        >
          <div
            style={{
              height:
                topSectionHeight !== null && topSectionHeight > 0
                  ? `${topSectionHeight}px`
                  : undefined,
              minHeight: 0,
              flexShrink: 0,
              flexGrow: 0,
              width: "100%",
            }}
            className="relative overflow-hidden"
          >
            <ThreeDView
              canvas3dRef={canvas3dRef}
              selectedPitches={selectedPitches}
              pitches={pitches}
              movements={movements}
              colorPalette={colorPalette}
              disabled={isLoading || isError || selectedPitches.size === 0}
              avatarState={{
                offsets: avatarOffsets,
                rotations: avatarRotations,
              }}
              playbackState={{
                isPlaying,
                playbackSpeed,
                minTime,
                maxTime,
                currentTime,
              }}
              playbackHandlers={{
                onPlayPause: handlePlayPause,
                onPlaybackSpeedChange: handlePlaybackSpeedChange,
                onSkipBackward: handleSkipBackward,
                onSkipForward: handleSkipForward,
                onTimelineChange: setCurrentTime,
              }}
              selectionHandlers={{
                onColorChangeAll: handleColorChangeAll,
                onOffsetChangeAll: handleOffsetChangeAll,
                onRotationChangeAll: handleRotationChangeAll,
              }}
              isHittingMode={isHittingMode}
            />
          </div>

          {topSectionHeight !== null && containerHeight > 0 && (
            <ResizableDivider
              onResize={handleDividerResize}
              containerHeight={containerHeight}
              currentTopHeight={topSectionHeight}
              containerRef={mainContentRef}
              minTopHeight={200}
              minBottomHeight={200}
            />
          )}

          <div
            style={{
              height:
                topSectionHeight !== null && containerHeight > 0
                  ? `${containerHeight - topSectionHeight - 4}px`
                  : undefined,
              minHeight: 0,
              flexShrink: 0,
              flexGrow: 0,
              width: "100%",
              overflow: "hidden",
            }}
            className="relative"
          >
            <ChartView
              dataColumns={timeSeriesColumns}
              selectedPitches={selectedPitches}
              pitches={pitches}
              movements={movements}
              currentTime={currentTime}
              onTimelineChange={setCurrentTime}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
